// =============================================================================
// Issue (§5.1). Member first, then one or more books in a single session.
//
// Shape of the screen, in order: find member → status panel with the
// eligibility banner → scan books into a basket → confirm → due date, large.
// =============================================================================

import { el, mount, formatDate, formatDateLong } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import {
  searchMembers, checkEligibility, activeLoansFor, getMemberSummary,
  findCopyByCode, activeLoanForCopy, issueBooks, normaliseCode,
} from '../lib/api.js';
import { createCodeInput } from '../lib/code-input.js';
import { emitDataChanged } from '../lib/events.js';
import { spinner } from '../lib/loading.js';
import { liveSearch } from '../lib/live-search.js';

// `preselectedId` arrives from #/issue/<memberId> — the Issue button on a
// member's own screen, where the student is already known.
export function issueScreen(root, preselectedId = null) {
  let member = null;
  let eligibility = null;
  let loans = [];
  let basket = [];       // [{ copy_id, copy_code, title }]
  let codeInput = null;
  let busy = false;
  // The live search on step 1, so it can be cancelled when the screen goes.
  let searchLive = null;

  const view = el('div', { class: 'screen' });
  mount(root, view);

  // --- Step 1: find the member ---------------------------------------------
  function renderSearch() {
    const input = el('input', {
      type: 'search', class: 'text-input',
      placeholder: T.searchPlaceholder,
      autocomplete: 'off', enterkeyhint: 'search',
      'aria-label': T.findMember,
    });
    const results = el('div', { class: 'results' });
    const btn = el('button', { class: 'btn btn-primary', type: 'submit' }, T.search);

    // Typing searches (lib/live-search.js). Two letters minimum: one letter
    // matches most of the branch and is never what anyone meant to look up.
    const live = liveSearch(input, async (term, stillCurrent) => {
      if (!term) { mount(results); return; }
      btn.disabled = true; btn.textContent = T.searching;
      try {
        const found = await searchMembers(term);
        if (!stillCurrent()) return;
        if (!found.length) {
          mount(results, el('p', { class: 'empty' }, T.noMembers));
        } else {
          mount(results, ...found.map((m) => el('button', {
            class: 'result-row', type: 'button',
            onClick: () => selectMember(m),
          },
            el('span', { class: 'result-name' }, m.full_name),
            el('span', { class: 'result-meta' },
              [m.member_code, m.guardian_phone].filter(Boolean).join(' · ')),
            // Surfacing the block here saves staff a step: they can see the
            // problem before committing to the member.
            !m.has_paid && el('span', { class: 'pill pill-warn' }, 'Payment pending'),
            m.overdue_loans > 0 && el('span', { class: 'pill pill-danger' }, 'Overdue'),
          )));
        }
      } catch (err) {
        if (stillCurrent()) mount(results, el('p', { class: 'error' }, err.message));
      } finally {
        btn.disabled = false; btn.textContent = T.search;
      }
    }, { min: 2 });

    // Enter runs the pending search now rather than waiting out the debounce.
    const run = (e) => { e?.preventDefault(); live.now(); };
    searchLive = live;

    // Staff opened this screen and looked for a scanner, because a search box
    // under a title reads as an optional filter. Naming it as step one of two,
    // and saying what step two is, is the whole fix.
    mount(view,
      el('h1', { class: 'screen-title' }, T.issueTitle),
      el('section', { class: 'card step-card' },
        el('h2', { class: 'step-title' }, T.issueStep1),
        el('p', { class: 'muted' }, T.issueStep1Hint),
        el('form', { class: 'search-row', onSubmit: run }, input, btn),
        results,
      ),
    );
    queueMicrotask(() => input.focus());
  }

  async function selectMember(m) {
    member = m;
    basket = [];
    // A fresh member gets a fresh scanner: cooldowns from the previous
    // student's session must not silently swallow a scan for this one.
    codeInput?.stop();
    codeInput = null;
    mount(view, spinner());
    await refreshMember();
  }

  // Entering at #/issue/<id>. If the member cannot be loaded — a stale link, a
  // member at another branch — fall back to the search rather than to an error
  // page: the desk still needs to issue a book.
  async function startWithMember(id) {
    mount(view, spinner());
    try {
      const m = await getMemberSummary(id);
      if (!m) return renderSearch();
      await selectMember(m);
    } catch {
      renderSearch();
    }
  }

  async function refreshMember() {
    try {
      [eligibility, loans] = await Promise.all([
        checkEligibility(member.member_id ?? member.id),
        activeLoansFor(member.member_id ?? member.id),
      ]);
      renderMember();
    } catch (err) {
      mount(view, el('p', { class: 'error' }, err.message),
        el('button', { class: 'btn', onClick: renderSearch }, T.cancel));
    }
  }

  // --- Step 2–4: status panel, basket, scanner -----------------------------
  //
  // The scanner control is created ONCE per member and never re-mounted.
  // Rebuilding it on every basket change detached the live <video> mid-decode,
  // leaving a camera that kept firing at a dead element — the "it just keeps
  // scanning" symptom staff saw when a book was already on loan.
  let statusBox = null, basketBox = null;

  function renderMember() {
    if (!codeInput) {
      codeInput = createCodeInput({
        label: T.scanOrType,
        onSubmit: addByCode,
        autoFocus: false,
        // A read closes the camera here too. Keeping it running between books
        // sounded right for a stack of three, but in practice the viewport
        // just sits there over the basket until the transaction is confirmed —
        // reported twice. Tapping Scan again for the next book is one tap; a
        // live camera nobody asked for is the whole rest of the screen.
        closeOnScan: true,
      });
    }
    statusBox = el('div', {});
    basketBox = el('div', { class: 'basket' });

    mount(view,
      el('div', { class: 'member-head' },
        el('div', {},
          el('h1', { class: 'screen-title' }, member.full_name),
          el('p', { class: 'muted' },
            [member.member_code, member.age_years ? `age ${member.age_years}` : null]
              .filter(Boolean).join(' · ')),
        ),
        el('button', { class: 'btn btn-sm', onClick: reset }, T.changeMember),
      ),
      statusBox,
      el('h2', { class: 'step-title' }, T.issueStep2),
      codeInput.root,
      basketBox,
    );
    renderStatus();
    renderBasket();
  }

  // Everything that moves when a book enters or leaves the basket — never the
  // scanner itself.
  function renderStatus() {
    const blocks = eligibility.blocks ?? [];
    const canBorrow = eligibility.can_borrow;
    // Slots used must reflect the basket live, so staff see the limit coming
    // rather than discovering it at confirm time (§5.1 step 4).
    const used = eligibility.slots_used + basket.length;
    const total = eligibility.slots_total;

    // Shut the scanner and the Add button when there is no slot left, so the
    // limit is visible before a book is presented rather than after.
    if (codeInput?.setDisabled) {
      codeInput.setDisabled(
        canBorrow && used >= total ? T.limitReachedHint(total) : null);
    }

    mount(statusBox,
      el('div', {
        class: `banner ${canBorrow ? 'banner-ok' : 'banner-blocked'}`,
        role: 'status',
      },
        el('strong', { class: 'banner-title' }, canBorrow ? T.canBorrow : T.blocked),
        ...blocks.map((b) => el('p', { class: 'banner-reason' }, b.message)),
      ),
      // Once the basket fills the last slot the green banner alone is
      // misleading — it reflects eligibility at load, not the basket. Say the
      // limit is reached rather than letting staff scan a book that will be
      // rejected.
      el('p', { class: `slots ${used >= total ? 'slots-full' : ''}` },
        used >= total ? T.slotsFull(used, total) : T.slotsUsed(used, total)),
      loans.length ? el('section', { class: 'card' },
        el('h2', { class: 'card-title' }, T.currentLoans),
        ...loans.map((l) => el('div', { class: 'loan-row' },
          el('span', { class: 'loan-title' }, l.title),
          el('span', { class: l.is_overdue ? 'pill pill-danger' : 'pill' },
            l.is_overdue ? T.daysOverdue(l.days_overdue) : `${T.dueBack} ${formatDate(l.due_at)}`),
        )),
      ) : null,
    );
  }

  function renderBasket() {
    const box = basketBox;
    const confirm = el('button', {
      class: 'btn btn-primary btn-large',
      disabled: basket.length === 0 || busy,
      onClick: doIssue,
    }, busy ? T.issuing
            : basket.length ? T.confirmIssue(basket.length)
            : T.confirmIssueEmpty);

    mount(box,
      el('h2', { class: 'card-title' }, T.basket),
      basket.length === 0
        ? el('p', { class: 'empty' }, T.basketEmpty)
        : el('div', {}, ...basket.map((b, i) => el('div', { class: 'basket-row' },
            el('span', { class: 'basket-title' }, b.title),
            el('span', { class: 'basket-code' }, b.copy_code),
            el('button', {
              class: 'btn btn-sm btn-danger', type: 'button',
              'aria-label': `${T.remove} ${b.title}`,
              onClick: () => { basket.splice(i, 1); renderStatus(); renderBasket(); },
            }, T.remove),
          ))),
      confirm,
    );
  }

  function flash(message, kind = 'error') {
    const note = el('p', { class: kind === 'error' ? 'error' : 'notice', role: 'alert' }, message);
    const box = view.querySelector('.basket');
    box?.prepend(note);
    setTimeout(() => note.remove(), 5000);
  }

  // --- Step 5: validate each scan immediately ------------------------------
  // How many more books this member may take, counting what is already in the
  // basket. Negative is impossible; zero means the limit is reached.
  function slotsLeft() {
    const total = eligibility?.slots_total;
    if (typeof total !== 'number') return Infinity;   // unknown: do not block
    return Math.max(0, total - (eligibility.slots_used + basket.length));
  }

  async function addByCode(raw) {
    const code = normaliseCode(raw);
    if (!code) return;

    // The limit is enforced in the database at confirm time (§6), which is
    // where it must live. But letting staff scan a fourth book and only then
    // refusing the whole transaction wastes the scan and reads as the app
    // changing its mind. Refuse the book, not the basket.
    if (slotsLeft() === 0) {
      flash(T.limitReachedFlash(eligibility.slots_total));
      return;
    }

    if (basket.some((b) => b.copy_code === code)) {
      flash('That book is already in the list.');
      return;
    }

    try {
      const copy = await findCopyByCode(code);
      // RLS hides other branches' copies, so "not found" and "another branch"
      // are indistinguishable here. The database gives the precise reason at
      // confirm time; this message covers both without being wrong.
      if (!copy) { flash(`Unknown book code: ${code}`); return; }

      if (copy.status !== 'available') {
        const name = copy.titles?.title ?? code;
        if (copy.status === 'on_loan') {
          // Naming the holder turns a dead end into an answer: staff can say
          // who has it without leaving the screen. A failed lookup must not
          // swallow the message that matters.
          const loan = await activeLoanForCopy(copy.id).catch(() => null);
          flash(loan
            ? `${name} is already on loan to ${loan.member_name}, due ${formatDate(loan.due_at)}.`
            : `${name} is already on loan.`);
        } else {
          flash(`${name} is marked ${copy.status} and cannot be issued.`);
        }
        return;
      }

      basket.push({
        copy_id: copy.id,
        copy_code: copy.copy_code,
        title: copy.titles?.title ?? copy.copy_code,
      });
      renderStatus();
      renderBasket();
    } catch (err) {
      flash(err.message);
    }
  }

  // --- Step 6–7: commit, then show the due date large ----------------------
  async function doIssue() {
    if (!basket.length || busy) return;
    busy = true; renderBasket();
    try {
      const created = await issueBooks(
        member.member_id ?? member.id,
        basket.map((b) => b.copy_id),
      );
      emitDataChanged();
      await codeInput?.stop();
      renderConfirmation(created?.[0]?.due_at, basket.slice());
      basket = [];
    } catch (err) {
      // The whole basket was rejected — the database is all-or-nothing here.
      // Keep the basket intact so staff can remove the offending book.
      busy = false;
      renderBasket();
      flash(err.message);
      return;
    }
    busy = false;
  }

  function renderConfirmation(dueAt, issued) {
    mount(view,
      el('div', { class: 'confirm' },
        el('h1', { class: 'confirm-title' }, T.issuedTitle),
        el('p', { class: 'confirm-sub' }, member.full_name),
        ...issued.map((b) => el('p', { class: 'confirm-item' }, b.title)),
        // The due date is the one thing staff read aloud to the parent, so it
        // is the largest element on the screen (§5.1 step 7).
        el('p', { class: 'confirm-label' }, T.dueBack),
        el('p', { class: 'confirm-date' }, formatDateLong(dueAt)),
      ),
      el('div', { class: 'confirm-actions' },
        el('button', { class: 'btn btn-primary btn-large', onClick: () => refreshMember() }, T.issueMore),
        el('button', { class: 'btn btn-large', onClick: reset }, T.done),
      ),
    );
  }

  async function reset() {
    await codeInput?.stop();
    member = null; eligibility = null; loans = []; basket = [];
    renderSearch();
  }

  if (preselectedId) startWithMember(preselectedId);
  else renderSearch();
  return { destroy: () => { searchLive?.stop(); codeInput?.stop(); } };
}
